import { useMutation } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ToggleManufacturerInput } from '@deltek/specification-client/core/Models/toggleManufacturerInput'
import {
    SetManufacturerProductListingIsSelectedMutation,
    SetManufacturerProductListingIsSelectedMutationForImportedPT,
} from 'api/graphQL/mutations/projectNugget.mutations'
import {
    setWorkspaceContents,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { ToggleImportedManufacturerInput } from 'api/graphQL/types/projectElement.types'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
    WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY,
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WorkspaceManufacturerListingItem,
} from 'shared/utils/workspaceUtils'
import { useShallowSelector } from '../../../Workspace2Store'
import { useProductFetcher } from '../../../api/useProductFetcher/useProductFetcher'
import { useTrackChangesQuery } from '../../../components/TrackChangesPanel/api'
import { ContentItemViewNodesType } from '../../../types'
import {
    createHyperlinkMutationInput,
    createServiceWorkerMessage,
} from '../../../utils/hyperlinkMutationUtils'
import { HyperlinkParentNode } from '../types'

// Constants
const SERVICE_WORKER_MESSAGE_DELAY_MS = 100

interface UseHyperlinkSelectionProps {
    parentNode: HyperlinkParentNode | null
}

/**
 * Hook to handle hyperlink selection logic
 */
export const useHyperlinkSelection = ({
    parentNode,
}: UseHyperlinkSelectionProps) => {
    const { projectId } = useParams<{ projectId?: string }>()

    const {
        contentItemViewNodes,
        setContentItemViewNodes,
        selectedProjectElementNode,
        familyAddedProductTypes,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        selectedProjectElementNode: state.selectedProjectElementNode as ProjectElementNode,
        familyAddedProductTypes: state.familyAddedProductTypes as ProjectElementNode[],
    }))

    const [setManufacturerProductListingIsSelected] = useMutation<
        { setManufacturerProductListingIsSelected: ContentItem[] },
        { input: Partial<ToggleManufacturerInput> }
    >(SetManufacturerProductListingIsSelectedMutation)

    const [setManufacturerProductListingIsSelectedForImportedPT] = useMutation<
        { setImportedManufacturer: ContentItem[] },
        { input: Partial<ToggleImportedManufacturerInput> }
    >(SetManufacturerProductListingIsSelectedMutationForImportedPT)

    // Get fetchTrackChangesData from the hook
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Get content view update functions from useProductFetcher
    const { getContentItemViewNodesAPI } = useProductFetcher({
        projectId,
        overrideContentItemNodes: true, // We want to update the nodes after selection
    })

    const handleSelectionOptionClick = useCallback(
        async (
            currentSelectedItem: Partial<TextContentElement>,
            isSelected: boolean
        ) => {
            // For hyperlinks, we need to call the setManufacturerProductListingIsSelected API
            if (!currentSelectedItem?.id) return

            if (!parentNode) {
                return
            }

            // Captured for rollback if the mutation below fails.
            let previousProductListings

            try {
                // Check if the hyperlink is currently selected
                const shouldSelect = !parentNode.isPillSelected

                // Perform optimistic UI update
                const updatedNodes = (contentItemViewNodes as ContentItemViewNodesType[]).map(
                    (node) => {
                        if (
                            node.elements?.some(
                                (el) => el.id === currentSelectedItem.id
                            )
                        ) {
                            return {
                                ...node,
                                isPillSelected: shouldSelect,
                            }
                        }
                        return node
                    }
                )
                setContentItemViewNodes(updatedNodes)

                // Keep productListings in sync with the optimistic update above.
                // reconcileManufacturerPillSelection (WorkspaceEditor.tsx) treats
                // productListings.isSelected as ground truth and re-syncs
                // contentItemViewNodes.isPillSelected from it on every
                // contentItemViewNodes change — without this, it immediately flips
                // the optimistic update back before the mutation below resolves.
                const listingId = parentNode.pillId || currentSelectedItem.id
                const { productListings } = workspaceContents()
                if (
                    productListings?.some(
                        (listing) => listing.listingId === listingId
                    )
                ) {
                    previousProductListings = productListings
                    setWorkspaceContents({
                        productListings: productListings.map((listing) =>
                            listing.listingId === listingId
                                ? { ...listing, isSelected: shouldSelect }
                                : listing
                        ),
                    })
                }

                // Create mutation inputs using utility functions
                const mutationParams = {
                    projectId: projectId || '',
                    selectedProjectElementNode,
                    familyAddedProductTypes,
                    parentNode,
                    currentSelectedItem,
                    isSelected: shouldSelect,
                }

                const { isImported, input } = createHyperlinkMutationInput(
                    mutationParams
                )

                // Call the appropriate mutation based on the node type
                const result = await (isImported
                    ? setManufacturerProductListingIsSelectedForImportedPT({
                          variables: { input },
                      })
                    : setManufacturerProductListingIsSelected({
                          variables: { input },
                      }))

                // Send messages to service worker for cross-window communication
                if (navigator.serviceWorker?.controller) {
                    setTimeout(() => {
                        const serviceWorkerMessage = createServiceWorkerMessage(
                            mutationParams
                        )

                        // Send selection state change
                        navigator.serviceWorker.controller?.postMessage({
                            [WORKSPACE_MANUFACTURER_SELECTED_KEY]: serviceWorkerMessage,
                        })

                        // Send BP/GBP update
                        navigator.serviceWorker.controller?.postMessage({
                            [WORKSPACE_MANUFACTURERS_LISTING_ITEMS_KEY]: {
                                listingId: serviceWorkerMessage.listingId,
                                isBP: false,
                                isGBP: false,
                            } as WorkspaceManufacturerListingItem,
                        })
                    }, SERVICE_WORKER_MESSAGE_DELAY_MS)
                }

                // Update track changes after successful hyperlink selection
                try {
                    await fetchTrackChangesData()
                } catch (error) {
                    // Don't throw - this is not critical to the selection operation
                }
            } catch (error) {
                // Revert optimistic update on error
                setContentItemViewNodes(contentItemViewNodes)
                if (previousProductListings) {
                    setWorkspaceContents({
                        productListings: previousProductListings,
                    })
                }
            }
        },
        [
            contentItemViewNodes,
            setContentItemViewNodes,
            setManufacturerProductListingIsSelected,
            setManufacturerProductListingIsSelectedForImportedPT,
            projectId,
            selectedProjectElementNode,
            parentNode,
            familyAddedProductTypes,
            fetchTrackChangesData,
            getContentItemViewNodesAPI,
        ]
    )

    return {
        handleSelectionOptionClick,
        isSelected: !!(parentNode as any)?.isPillSelected,
    }
}
